import { Dispatch, SetStateAction, useCallback, useLayoutEffect, useMemo, useState } from "react";
import { useNLE } from "@features/cart/hook/useNLE";
import { EEirSalesNLEStatus } from "@modules/eirSalesFacade/constants";
import { useAscertain } from "@modules/eirSalesFacade/swr/ascertain";
import { useSaveAscertain } from "@features/cart/hook/useSaveAscertain";
import { ICart, IService } from "@features/cart/ICart";
import { EServiceTechnology } from "@constants";
import { EBroadbandKeyError } from "./BroadbandErrorContent/BroadbandErrorContent";
import { useStyle } from "./style";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import type { FieldName, FieldValues } from "react-hook-form";
import { getObjectByPath } from "@utils/getObjectByPath";
import { EYesNoNoneValues } from "@interface/EYesNoNoneValues";

interface IUseBroadbandCardParams {
    onDelete?: () => void;
    prequalId: string;
    offerName: string;
    offerId: number;
    services: IService[];
    onAnswerChange?(): void;
    setCart: Dispatch<SetStateAction<ICart | undefined>>;
}

export const useBroadbandCard = (params: IUseBroadbandCardParams) => {
    const { prequalId, offerId, services, setCart, offerName } = params;
    const { classes } = useStyle();
    const { t } = useTranslation("cart");
    const {
        clearErrors,
        errors,
        formState: { submitCount },
        register,
        trigger,
    } = useFormContext<FieldValues>();

    const hasSubmitted = submitCount > 0;
    const { nle, error: nleError, isPending } = useNLE({
        prequalId,
    });
    const { data: ascertain, isLoading: isLoadingAscertain, mutate: mutateAscertain } = useAscertain(offerId);
    const uan = ascertain?.broadbandAccountNumber;

    const { saveAscertain, isLoading: isLoadingSave, error: saveAscertainError } = useSaveAscertain({
        offerId,
        setCart,
    });

    const hasAvailableSlot =
        services.find((service) => service.broadbandTechnology === EServiceTechnology.FTTH)?.slotAvailable ?? false;

    const hasActiveBroadband =
        nle?.status === EEirSalesNLEStatus.SUCCESS &&
        (Number(nle.activePathsQuantity) > 0 || Number(nle.activeFthPathsQuantity) > 0);

    const hasNoActiveBroadband =
        nle?.status === EEirSalesNLEStatus.SUCCESS &&
        Number(nle.activePathsQuantity) === 0 &&
        Number(nle.activeFthPathsQuantity) === 0;

    const determineBroadbandKey = useCallback((): EBroadbandKeyError => {
        if (hasNoActiveBroadband && !hasAvailableSlot) {
            return EBroadbandKeyError.UNAVAILABLE;
        }

        return EBroadbandKeyError.DEFAULT;
    }, [hasAvailableSlot, hasNoActiveBroadband]);

    const [providerRadioValue, setProviderRadioValue] = useState<EYesNoNoneValues>(EYesNoNoneValues.NONE);
    const [suppressErrorsUntilSubmitCount, setSuppressErrorsUntilSubmitCount] = useState(0);
    const [hasProviderAgreeConfirmed, setHasProviderAgreeConfirmed] = useState(false);

    const broadbandPrefix = useMemo(() => `broadband.${offerId}`, [offerId]);
    const broadbandValidFieldName = useMemo(() => `${broadbandPrefix}.isValid`, [broadbandPrefix]);
    const broadbandRhfErrorMessage = useMemo(
        () => getObjectByPath<string>(errors, `${broadbandPrefix}.isValid.message`),
        [broadbandPrefix, errors]
    );

    const shouldShowErrors = hasSubmitted && submitCount > suppressErrorsUntilSubmitCount;

    const shouldForceValidateUanForm = shouldShowErrors && hasActiveBroadband && !uan;
    const shouldForceValidateProviderQuestion = shouldShowErrors && hasNoActiveBroadband && hasAvailableSlot;

    const handleProviderRadioValueChange = useCallback(
        (value: EYesNoNoneValues) => {
            setProviderRadioValue(value);
            setSuppressErrorsUntilSubmitCount(submitCount);
            setHasProviderAgreeConfirmed(false);
        },
        [submitCount]
    );

    const handleAgreeConfirmed = useCallback(() => {
        setHasProviderAgreeConfirmed(true);
    }, []);

    const isBroadbandValid = useMemo(() => {
        // Must remove broadband first
        if ((hasNoActiveBroadband && !hasAvailableSlot) || nleError) {
            return false;
        }

        // Active broadband requires UAN to be confirmed
        if (hasActiveBroadband) {
            return Boolean(uan);
        }

        // No active broadband: question must be answered and, if Yes, user must agree & continue
        if (hasNoActiveBroadband && hasAvailableSlot) {
            if (providerRadioValue === EYesNoNoneValues.NONE) {
                return false;
            }

            if (providerRadioValue === EYesNoNoneValues.YES) {
                return Boolean(ascertain?.hasActiveBroadband) || hasProviderAgreeConfirmed;
            }

            return true;
        }

        return true;
    }, [
        ascertain?.hasActiveBroadband,
        hasActiveBroadband,
        hasAvailableSlot,
        hasNoActiveBroadband,
        hasProviderAgreeConfirmed,
        nleError,
        providerRadioValue,
        uan,
    ]);

    useLayoutEffect(() => {
        if (!hasSubmitted) {
            return;
        }
        if (!providerRadioValue || providerRadioValue === EYesNoNoneValues.NONE) {
            // Keep validation consistent with provider state.
        }

        if (hasProviderAgreeConfirmed) {
            return;
        }

        void trigger(broadbandValidFieldName as FieldName<FieldValues>);
    }, [
        broadbandValidFieldName,
        hasActiveBroadband,
        hasAvailableSlot,
        hasSubmitted,
        hasNoActiveBroadband,
        hasProviderAgreeConfirmed,
        nleError,
        providerRadioValue,
        trigger,
        uan,
    ]);

    useLayoutEffect(() => {
        if (!hasSubmitted) {
            return;
        }
        if (!isBroadbandValid) {
            return;
        }

        clearErrors(broadbandValidFieldName as FieldName<FieldValues>);
    }, [broadbandValidFieldName, clearErrors, hasSubmitted, isBroadbandValid]);

    const broadbandHiddenInputRef = register(broadbandValidFieldName as FieldName<FieldValues>, {
        validate: () => {
            // Must remove broadband first
            if ((hasNoActiveBroadband && !hasAvailableSlot) || nleError) {
                return t("broadband.error.banner");
            }

            // Active broadband requires UAN to be confirmed
            if (hasActiveBroadband) {
                return Boolean(uan) || t("broadband.UAN.validationError");
            }

            // No active broadband: question must be answered and, if Yes, user must agree & continue
            if (hasNoActiveBroadband && hasAvailableSlot) {
                if (providerRadioValue === EYesNoNoneValues.NONE) {
                    return t("broadband.noOptionSelectedValidationError");
                }

                if (providerRadioValue === EYesNoNoneValues.YES) {
                    return (
                        Boolean(ascertain?.hasActiveBroadband) ||
                        hasProviderAgreeConfirmed ||
                        t("broadband.agreeValidationError")
                    );
                }

                return true;
            }

            return true;
        },
    });

    const errorCardClassName = useMemo(() => {
        if (!shouldShowErrors || isBroadbandValid) {
            return classes.card;
        }

        return `${classes.card} ${classes.cardError}`;
    }, [classes.card, classes.cardError, isBroadbandValid, shouldShowErrors]);

    const broadbandErrorMessage = useMemo(
        () => broadbandRhfErrorMessage ?? t("broadband.noOptionSelectedValidationError"),
        [broadbandRhfErrorMessage, t]
    );

    return {
        nleError,
        isPending,
        ascertain,
        isLoadingAscertain,
        uan,
        saveAscertain,
        isLoadingSave,
        saveAscertainError,
        mutateAscertain,
        hasAvailableSlot,
        hasActiveBroadband,
        hasNoActiveBroadband,
        providerRadioValue,
        setProviderRadioValue: handleProviderRadioValueChange,
        handleAgreeConfirmed,
        determineBroadbandKey,
        broadbandPrefix,
        broadbandValidFieldName,
        broadbandHiddenInputRef,
        isBroadbandValid,
        shouldForceValidateUanForm,
        shouldForceValidateProviderQuestion,
        errorCardClassName,
        broadbandErrorMessage,
        offerId,
        setCart,
        offerName,
        shouldShowErrors,
        showBroadbandError: shouldShowErrors && !isBroadbandValid,
    };
};
